import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getAllTestimonials } from "../../store/common/testimonialSlice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
} from "../../components/admin/AdminPageShell";

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const card = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const Testimonials = () => {
  const dispatch = useAppDispatch();
  const { testimonials, loading, error } = useAppSelector(
    (state) => state.testimonial,
  );
  const [copied, setCopied] = useState(false);
  const url = "https://toothsketch.com/add/testimonial";

  const handleCopy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  useEffect(() => {
    dispatch(getAllTestimonials());
  }, [dispatch]);

  return (
    <>
      <Helmet>
        <title>All Testimonials</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Testimonials"
          subtitle="Patient reviews collected from the public testimonial form"
        />

        {loading ? (
          <div className="min-h-[40vh] flex items-center justify-center text-[#8A8A85]">
            <span className="inline-flex flex-col items-center gap-2">
              <span
                className="h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
                aria-hidden
              />
              Loading testimonials…
            </span>
          </div>
        ) : error ? (
          <div className="min-h-[40vh] flex items-center justify-center text-red-500">
            {error}
          </div>
        ) : (
          <section className="relative py-6 px-4 sm:px-6 lg:px-10 overflow-hidden rounded-3xl bg-gradient-to-b from-gray-50 to-white">
            {/* Background blobs */}
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#2fa9bf]/20 rounded-full blur-3xl" />
            <div className="absolute top-1/2 -right-24 w-96 h-96 bg-gray-200/60 rounded-full blur-3xl" />

            <motion.div
              variants={container}
              initial="hidden"
              animate="visible"
              className="relative max-w-7xl mx-auto"
            >
              {/* Header */}
              <motion.div variants={card} className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
                  Because Every Smile Has a Story
                </h2>
                <p className="mt-4 text-gray-600 max-w-xl mx-auto">
                  Personal experiences shared by our valued patients
                </p>
                <p
                  onClick={handleCopy}
                  className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-[#2fa9bf] hover:text-[#249aad] transition"
                >
                  {copied ? "URL Copied ✓" : "Add URL"}
                </p>
              </motion.div>

              {/* Cards */}
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {testimonials.map((item) => (
                  <motion.div
                    key={item.id}
                    variants={card}
                    whileHover={{ y: -10 }}
                    className="group relative rounded-3xl bg-white/70 backdrop-blur-xl border border-white/40 p-7 shadow-[0_20px_60px_rgba(0,0,0,0.08)] transition-all"
                  >
                    {/* Quote mark */}
                    <div className="text-6xl leading-none text-[#2fa9bf]/20 mb-3 select-none">
                      “
                    </div>

                    {/* Review */}
                    <p className="text-gray-700 leading-relaxed text-[15px] mb-8">
                      {item.review}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center gap-4 mt-auto">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#2fa9bf] to-[#6fd3e3] flex items-center justify-center text-white font-semibold text-lg shadow-md">
                        {item.author.charAt(0)}
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-900 leading-none">
                          {item.author}
                        </h4>
                        <p className="text-sm text-gray-500 mt-1">{item.region}</p>
                      </div>
                    </div>

                    {/* Hover glow */}
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#2fa9bf]/10 to-[#6fd3e3]/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                  </motion.div>
                ))}
              </div>

              {/* Empty State */}
              {testimonials.length === 0 && (
                <div className="text-center text-gray-500 mt-16">
                  No testimonials available yet.
                </div>
              )}
            </motion.div>
          </section>
        )}
      </AdminPageShell>
    </>
  );
};

export default Testimonials;
